import { formatDate } from '@angular/common';
import { Component, ElementRef, forwardRef, HostListener, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { isEqual } from 'app/shared/utils';
import { DatePickerComponent } from './date-picker.component';


type Range = { days?: number; months?: number | 'last' | 'this'; year?: number | 'last' | 'this' };

type SelectOptions = 'none' | Range | 'custom';

export type DateRange = { range: 'none' } | { range: Range } | { range: 'custom', low?: string, high?: string };


@Component({
    selector: 'ui-date-range',
    imports: [
        ReactiveFormsModule,
        DatePickerComponent,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateRangeComponent),
            multi: true,
        },
    ],
    template: `
        <div [formGroup]="form">
            <select
                #selectInput
                class="form-control"
                formControlName="range"
                (ngModelChange)="rangeChange($event)"
                [compareWith]="equals">
                <option value="none">Nothing Selected</option>
                <option [ngValue]="{ months: 24 }">24 Months Back</option>
                <option [ngValue]="{ months: 18 }">18 Months Back</option>
                <option [ngValue]="{ months: 12 }">12 Months Back</option>
                <option [ngValue]="{ months: 6 }">6 Months Back</option>
                <option [ngValue]="{ days: 90 }">90 Days Back</option>
                <option [ngValue]="{ days: 60 }">60 Days Back</option>
                <option [ngValue]="{ days: 30 }">30 Days Back</option>
                <option [ngValue]="{ days: 7 }">7 Days Back</option>
                <option [ngValue]="{ months: 'last' }">Last Month</option>
                <option [ngValue]="{ year: 'last' }">Last Year</option>
                <option [ngValue]="{ months: 'this' }">This Month</option>
                <option [ngValue]="{ year: 'this' }">This Year</option>
                <option value="custom">Custom</option>
            </select>
            @if (showDatePickers()) {
                <div class="form-row" animate.enter="slide-up-down-enter" animate.leave="slide-up-down-leave">
                    <div class="col-md-6">
                        <label class="small">From</label>
                        <ui-date-picker formControlName="low"></ui-date-picker>
                    </div>
                    <div class="col-md-6">
                        <label class="small">To</label>
                        <ui-date-picker formControlName="high"></ui-date-picker>
                    </div>
                </div>
            }
        </div>
    `,
})
export class DateRangeComponent implements ControlValueAccessor {

    private selectInput = viewChild.required<ElementRef>('selectInput');

    showDatePickers = signal(false);

    private onChange = (_: any) => {};

    private onTouched = () => {};

    form = new FormGroup({
        range: new FormControl<'none' | Range | 'custom'>('none'),
        low: new FormControl<string | undefined>(undefined),
        high: new FormControl<string | undefined>(undefined),
    });

    constructor() {
        this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
            if (value?.range) {
                this.onChange(value.range === 'none' ? null : value);
                this.onTouched();
            }
        });
    }

    /**
     * [for] directive handler
     */
    @HostListener('click')
    onHostClick() {
        this.selectInput().nativeElement.click();
    }

    equals(first: SelectOptions, second: SelectOptions) {
        return isEqual(first, second);
    }

    rangeChange(value: SelectOptions) {
        this.updateDates(value);
    }

    private updateDates(value: SelectOptions | 'custom') {
        if (value === 'custom') {
            this.showDatePickers.set(true);
            return;
        }
        this.showDatePickers.set(false);
        if (!value || value === 'none' || typeof value !== 'object') {
            this.form.patchValue({
                low: undefined,
                high: undefined,
            });
            return;
        }
        let low = new Date();
        let high = new Date();
        if (value.year) {
            if (value.year === 'this') {
                low = new Date(low.getFullYear(), 0, 1);
            } else if (value.year === 'last') {
                low = new Date(low.getFullYear() - 1, 0, 1);
                high = new Date(high.getFullYear() - 1, 11, 31);
            }
        }
        if (value.months) {
            if (value.months === 'this') {
                low = new Date(low.getFullYear(), low.getMonth(), 1);
            } else if (value.months === 'last') {
                low = new Date(low.getFullYear(), low.getMonth() - 1, 1);
                high = new Date(new Date(high.getFullYear(), high.getMonth(), 1).getTime() - 1);
            } else {
                low = new Date(low.getFullYear(), low.getMonth() - value.months, 1);
            }
        }
        if (value.days) {
            low.setDate(low.getDate() - value.days);
        }
        this.form.patchValue({
            low: formatDate(low, 'yyyy-MM-dd', 'en_US'),
            high: formatDate(high, 'yyyy-MM-dd', 'en_US'),
        });
    }

    writeValue(obj: DateRange) {
        if (typeof obj !== 'object' || obj === null) {
            return;
        }
        this.form.patchValue(obj);
        this.updateDates(obj.range || 'none');
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }
}
